// Columna derecha del explorer: encabezado como el de Inventario, «Iniciar venta» y pestañas
// Producto · Promociones · Ventas hoy · Similares. Lo que el catálogo no publica no se dibuja.
import type { ReactNode } from 'react';
import { Loader2, PanelLeft, PackageSearch, Plus } from 'lucide-react';
import { skuSoldCents } from '@/api/services/retail-wizard/soldAmount';
import { Badge } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { DetailTabs, type DetailTab } from '@/shared/patterns/DetailCard';
import { DetailHeader, type DetailHeaderFact } from '@/shared/patterns/DetailHeader';
import { KindChip } from '@/shared/patterns/KindChip';
import { PromotionPill, PromotionsTab, promotionsTabLabel } from '@/shared/patterns/Promotions';
import { StatusPill } from '@/shared/patterns/Pills';
import { StockAlertChip } from '@/shared/patterns/StockAlertChip';
import { productTierLabel } from '@/shared/lib/format/productTier';
import { formatMXN } from '@/shared/lib/format/money';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { formatTimeMx } from '@/lib/dateHelpers';
import { isService } from '@/domains/sales/wizard/catalogAvailability';
import type { PortalCatalogRow, PortalLevelPlan } from '@/api/services/retail-wizard/contract';
import type { ScopeWidePromotion } from '@/domains/sales/wizard/types';
import { financierChips } from '../../pos/lib/financierChips';
import {
    availableColors,
    catalogAlerts,
    catalogKindOf,
    categoryLabel,
    modelLabel,
    priorityLabel,
    productLabel,
    productVariant,
    similarProducts,
    stockLabelOf,
    stockTone,
    stockToneOf,
} from '../lib/catalogExplorer';
import {
    OWN_SALES_ONLY_NOTE,
    RECENT_WINDOW_DAYS,
    skuSalesFacts,
    type SkuSalesFacts,
} from '../lib/catalogSales';
import { levelLabel, levelEligibility } from '../lib/catalogLevels';
import type { DeepLinkState } from '../lib/catalogList';
import type { RecentSale } from '../../api/usePortal';
import {
    catalogPromotionsState,
    skuPromotionSummaries,
    undetailedPromotionReason,
} from '../lib/catalogPromotions';
import { DetailSection, EMPTY_NOTE } from './DetailSection';

export const DETAIL_TABS = ['producto', 'promociones', 'ventas', 'similares'] as const;
export type ProductDetailTab = (typeof DETAIL_TABS)[number];

const TAB_LABEL: Record<ProductDetailTab, string> = {
    producto: 'Producto',
    promociones: 'Promociones',
    ventas: 'Ventas hoy',
    similares: 'Similares',
};

/** Las pestañas; «Promociones» lleva su contador en el texto. */
function detailTabs(promotionsLabel: string): DetailTab<ProductDetailTab>[] {
    return DETAIL_TABS.map((key) => ({
        key,
        label: key === 'promociones' ? promotionsLabel : TAB_LABEL[key],
    }));
}

const PENDING_CELL = '—';

const STOCK_CLASS: Record<ReturnType<typeof stockTone>, string> = {
    ok: 'text-foreground',
    low: 'font-semibold text-warning',
    out: 'font-semibold text-destructive',
};

/** Lo que la pestaña «Ventas hoy» necesita de la lectura de ventas, sin acoplarse a la query. */
export interface SalesState {
    rows: RecentSale[];
    isLoading: boolean;
    error: Error | null;
    /** `store` son las ventas de toda la tienda; `own`, solo las del vendedor. */
    scope: 'store' | 'own';
}

/** Planes por nivel de la primera página; sin planes, `reason` dice por qué. */
export interface LevelsState {
    storeId: string;
    plans: PortalLevelPlan[] | null;
    reason: string | null;
    isLoading: boolean;
}

/** El `?sku=` que todavía no se abre: se busca, no llegó entre lo cargado, o no existe. */
export type PendingLink = Extract<DeepLinkState, 'searching' | 'beyondLoaded' | 'notFound'>;

export function ProductDetailPanel({
    item,
    catalog,
    sales,
    levels,
    scopeWidePromotions,
    tab,
    onTabChange,
    onSell,
    onSelectSku,
    pendingLink = null,
    loadedCount = 0,
    catalogComplete = true,
}: {
    item: PortalCatalogRow | null;
    /** Lo cargado del catálogo: colores del modelo y similares. */
    catalog: PortalCatalogRow[];
    sales: SalesState;
    levels: LevelsState;
    /** Promos de toda la tienda (primera página); aplican a cualquier producto. */
    scopeWidePromotions?: readonly ScopeWidePromotion[];
    tab: ProductDetailTab;
    onTabChange: (tab: ProductDetailTab) => void;
    onSell: (sku: string) => void;
    onSelectSku: (sku: string) => void;
    /** El `?sku=` de la URL que no se abre: se dice, no se abre la ficha de otro equipo. */
    pendingLink?: PendingLink | null;
    /** Cuántos productos van cargados, para el aviso del enlace que no llegó. */
    loadedCount?: number;
    /** Ya no quedan páginas: colores y similares miran el catálogo entero. */
    catalogComplete?: boolean;
}) {
    if (pendingLink === 'searching') {
        return (
            <MasterDetailEmpty
                icon={<Loader2 className="size-8 animate-spin" aria-hidden />}
                title="Buscando ese equipo…"
                sub="Lo estamos buscando entre las páginas del catálogo de tu tienda."
            />
        );
    }
    if (pendingLink === 'beyondLoaded') {
        return (
            <MasterDetailEmpty
                icon={<PackageSearch className="size-8" aria-hidden />}
                title={`Ese equipo no está entre los ${String(loadedCount)} productos cargados`}
                sub="Búscalo por marca o modelo, o usa «Cargar más» al pie de la tabla para traer el resto."
            />
        );
    }
    if (pendingLink === 'notFound') {
        return (
            <MasterDetailEmpty
                icon={<PackageSearch className="size-8" aria-hidden />}
                title="No se encontró ese equipo"
                sub="El enlace apunta a un SKU que no está en el catálogo de tu tienda. Búscalo por marca o modelo."
            />
        );
    }

    if (!item) {
        return (
            <MasterDetailEmpty
                icon={<PanelLeft className="size-8 opacity-40" aria-hidden />}
                title="Selecciona un producto para ver su detalle"
            />
        );
    }

    const soldOut = stockToneOf(item) === 'out';
    const promotions = catalogPromotionsState(item, scopeWidePromotions, levels.isLoading);

    return (
        <>
            <ProductHeader item={item} />

            {/* .detail-actions */}
            <div className="flex flex-none gap-1.5 border-b border-border px-3.5 py-2.5">
                <button
                    type="button"
                    disabled={soldOut}
                    title={soldOut ? 'No hay unidades de este equipo en tu tienda.' : undefined}
                    onClick={() => {
                        onSell(item.sku);
                    }}
                    className="inline-flex h-[28px] flex-1 items-center justify-center gap-1.5 rounded-[5px] bg-primary px-2 text-[11.5px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <Plus className="size-3" aria-hidden />
                    Iniciar venta con este producto
                </button>
            </div>

            <DetailTabs
                tabs={detailTabs(promotionsTabLabel(promotions))}
                active={tab}
                onChange={onTabChange}
            />

            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3 text-[12px]">
                {tab === 'producto' ? (
                    <ProductTab item={item} catalog={catalog} levels={levels} />
                ) : tab === 'promociones' ? (
                    <PromotionsTab state={promotions} />
                ) : tab === 'ventas' ? (
                    <SalesTab item={item} sales={sales} />
                ) : (
                    <SimilarTab
                        item={item}
                        catalog={catalog}
                        catalogComplete={catalogComplete}
                        onSelectSku={onSelectSku}
                    />
                )}
            </div>
        </>
    );
}

/** Encabezado de la ficha como el de Inventario: tipo, gama, promoción, SKU copiable, alertas y datos. */
function ProductHeader({ item }: { item: PortalCatalogRow }) {
    const tone = stockToneOf(item);
    const alerts = catalogAlerts(item);
    const financiers = financierChips(item.eligibleFinanciers);
    const facts: DetailHeaderFact[] = [
        { key: 'price', label: 'Contado', value: formatMXN(item.cashPriceCents / 100) },
        isService(item)
            ? { key: 'stock', label: 'Stock', value: 'Sin inventario', wrap: true }
            : {
                  key: 'stock',
                  label: 'Stock',
                  value: String(item.stock ?? 0),
                  sub: 'en tu tienda',
                  tone: tone === 'out' ? 'danger' : undefined,
              },
        {
            key: 'financiers',
            label: 'Financieras',
            value: String(financiers.length),
            sub:
                financiers.length === 0
                    ? 'Sin financieras configuradas'
                    : financiers.map((c) => c.short).join(' · '),
        },
    ];
    return (
        <section aria-label="Resumen del producto" className="flex-none">
            <DetailHeader
                name={productLabel(item)}
                badges={
                    <>
                        <KindChip kind={catalogKindOf(item)} />
                        {item.tier.trim() === '' ? null : (
                            <StatusPill tone="neutral">{productTierLabel(item.tier)}</StatusPill>
                        )}
                        <PromotionPill
                            promotions={skuPromotionSummaries(item)}
                            undetailedReason={undetailedPromotionReason(item)}
                            showKind
                            className="max-w-[160px]"
                        />
                    </>
                }
                subItems={[productVariant(item)]}
                idLabel="SKU"
                idValue={item.sku}
                note={
                    alerts.length === 0 ? undefined : (
                        <ul aria-label="Alertas" className="flex flex-wrap gap-1">
                            {alerts.map((alert) => (
                                <StockAlertChip
                                    key={alert.key}
                                    as="li"
                                    alert={alert.key}
                                    title={alert.text}
                                >
                                    {alert.label}
                                </StockAlertChip>
                            ))}
                        </ul>
                    )
                }
                facts={facts}
            />
        </section>
    );
}

function InfoRow({ label, value }: { label: ReactNode; value: ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-2 py-[3px]">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right font-medium">{value}</span>
        </div>
    );
}

function ProductTab({
    item,
    catalog,
    levels,
}: {
    item: PortalCatalogRow;
    catalog: PortalCatalogRow[];
    levels: LevelsState;
}) {
    const colors = availableColors(item, catalog);
    const chips = financierChips(item.eligibleFinanciers);

    return (
        <>
            {colors.length === 0 ? null : (
                <DetailSection title="Colores disponibles">
                    <div className="flex flex-wrap gap-1">
                        {colors.map((color) => (
                            <Badge
                                key={color}
                                variant="muted"
                                size="xs"
                                className="px-2 py-0.5 text-[10.5px]"
                            >
                                {color}
                            </Badge>
                        ))}
                    </div>
                </DetailSection>
            )}

            <DetailSection title="Datos comerciales">
                <InfoRow label="SKU" value={<span className="font-mono">{item.sku}</span>} />
                <InfoRow label="Marca" value={item.brand || PENDING_CELL} />
                <InfoRow label="Modelo" value={modelLabel(item) || PENDING_CELL} />
                <InfoRow label="Precio contado" value={formatMXN(item.cashPriceCents / 100)} />
                <InfoRow
                    label="Stock actual"
                    value={
                        <span className={STOCK_CLASS[stockToneOf(item)]}>{stockLabelOf(item)}</span>
                    }
                />
                <InfoRow label="Categoría" value={categoryLabel(item)} />
                <InfoRow label="Prioridad de venta" value={priorityLabel(item) ?? PENDING_CELL} />
            </DetailSection>

            <DetailSection title="Elegibilidad crédito SureKeep">
                <LevelEligibilityRows item={item} levels={levels} />
            </DetailSection>

            <DetailSection title="Financieras que aceptan este producto">
                {chips.length === 0 ? (
                    <p className={EMPTY_NOTE}>
                        El catálogo de tu tienda no tiene financieras configuradas para este
                        producto.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {chips.map((chip) => (
                            <Badge
                                key={chip.code}
                                variant={chip.tone}
                                size="xs"
                                className="text-[10px]"
                            >
                                {chip.name}
                            </Badge>
                        ))}
                    </div>
                )}
            </DetailSection>
        </>
    );
}

function LevelEligibilityRows({ item, levels }: { item: PortalCatalogRow; levels: LevelsState }) {
    if (levels.isLoading) return <p className={EMPTY_NOTE}>Cargando los planes por nivel…</p>;
    if (levels.plans === null || levels.plans.length === 0) {
        return <p className={EMPTY_NOTE}>{levels.reason}</p>;
    }
    return (
        <>
            {levels.plans.map((plan) => {
                const result = levelEligibility(item, plan);
                return (
                    <InfoRow
                        key={plan.level}
                        label={`Nivel ${levelLabel(plan.level)}`}
                        value={
                            result.kind === 'eligible' ? (
                                <span className="text-success">Elegible</span>
                            ) : result.kind === 'unknown' ? (
                                <span
                                    className="text-muted-foreground"
                                    title="No se pudo leer el plan de este nivel para este equipo."
                                >
                                    Sin dato
                                </span>
                            ) : (
                                <span className="text-destructive">
                                    {result.kind === 'cap'
                                        ? `Excede límite (${formatMXN(result.maxFinancedCents / 100)})`
                                        : 'SureKeep no lo financia'}
                                </span>
                            )
                        }
                    />
                );
            })}
        </>
    );
}

function SalesTab({ item, sales }: { item: PortalCatalogRow; sales: SalesState }) {
    if (sales.isLoading) return <p className={EMPTY_NOTE}>Cargando las ventas de este SKU…</p>;
    // Una lectura fallida no se pinta como «sin ventas»: son cosas distintas.
    if (sales.error) {
        return (
            <p className={EMPTY_NOTE}>
                No se pudieron leer las ventas, así que no se puede saber cuántas veces se colocó
                este SKU.
            </p>
        );
    }

    const facts = skuSalesFacts(item.sku, sales.rows);
    const store = sales.scope === 'store';

    return (
        <>
            <DetailSection
                title={store ? 'Ventas hoy de este SKU en mi tienda' : 'Mis ventas hoy de este SKU'}
            >
                <TodaySales facts={facts} sku={item.sku} />
            </DetailSection>
            <DetailSection
                title={
                    store
                        ? `Ventas ${String(RECENT_WINDOW_DAYS)} días · toda la tienda`
                        : `Mis ventas ${String(RECENT_WINDOW_DAYS)} días`
                }
            >
                {facts.recent.length === 0 ? (
                    <p className={EMPTY_NOTE}>
                        {store
                            ? 'Sin ventas recientes de este SKU en mi tienda.'
                            : 'Sin ventas recientes de este SKU.'}
                    </p>
                ) : (
                    <>
                        <div className="text-[12px]">
                            {facts.recent.length} {facts.recent.length === 1 ? 'venta' : 'ventas'}{' '}
                            en los últimos {RECENT_WINDOW_DAYS} días.
                        </div>
                        {facts.recentVolumeCents === null ? null : (
                            <div className="mt-1 text-[10.5px] text-muted-foreground">
                                Volumen: {formatMXN(facts.recentVolumeCents / 100)}
                            </div>
                        )}
                    </>
                )}
            </DetailSection>
            {store ? null : (
                <p className="text-[11px] text-muted-foreground">{OWN_SALES_ONLY_NOTE}</p>
            )}
        </>
    );
}

function TodaySales({ facts, sku }: { facts: SkuSalesFacts; sku: string }) {
    if (facts.today.length === 0) {
        return <p className={EMPTY_NOTE}>Sin ventas hoy de este SKU.</p>;
    }
    return (
        <>
            {facts.today.map((sale) => (
                <InfoRow
                    key={sale.saleId}
                    label={formatTimeMx(sale.soldAt)}
                    value={`${saleCustomerLabel(sale) ?? 'Cliente sin nombre'} · ${formatMXN(skuSoldCents(sale, sku) / 100)}`}
                />
            ))}
        </>
    );
}

function SimilarTab({
    item,
    catalog,
    catalogComplete,
    onSelectSku,
}: {
    item: PortalCatalogRow;
    catalog: PortalCatalogRow[];
    catalogComplete: boolean;
    onSelectSku: (sku: string) => void;
}) {
    const similar = similarProducts(item, catalog);

    return (
        <DetailSection title="Productos comparables · misma banda de precio">
            {catalogComplete ? null : (
                <p className="mb-1 text-[10.5px] text-muted-foreground">
                    Entre los productos cargados; «Cargar más» suma el resto.
                </p>
            )}
            {similar.length === 0 ? (
                <p className={EMPTY_NOTE}>Sin productos comparables en la banda de precio.</p>
            ) : (
                similar.map((candidate) => (
                    <button
                        key={candidate.sku}
                        type="button"
                        onClick={() => {
                            onSelectSku(candidate.sku);
                        }}
                        className="flex w-full items-baseline justify-between gap-2 rounded px-1 py-[3px] text-left transition-colors hover:bg-accent/40"
                    >
                        <span className="truncate text-muted-foreground">
                            {productLabel(candidate)}
                            <span className="ml-1 font-mono text-[9.5px]">{candidate.sku}</span>
                        </span>
                        <span className="whitespace-nowrap font-medium tabular-nums">
                            {formatMXN(candidate.cashPriceCents / 100)}
                        </span>
                    </button>
                ))
            )}
        </DetailSection>
    );
}
